export const COMMISSION_CATEGORIES = [
  { id: "mortgage", label: "Mortgage" },
  { id: "lifetime", label: "Lifetime" },
  { id: "protection", label: "Protection" },
  { id: "gi", label: "GI" },
  { id: "pension", label: "Pension" },
  { id: "investment", label: "Investment" },
  { id: "longTermCare", label: "Long Term Care" },
  { id: "bridgingAndCommercial", label: "Bridging and Commercial" },
  { id: "ongoing", label: "On-going" },
  { id: "other", label: "Other" },
];

const text = (value) => String(value || "").trim().toLowerCase();
const amountOf = (row) => {
  const amount = Number(String(
    row?.PaymentAmount ?? row?.paymentAmount ?? 0,
  ).replace(/[,£]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

export const commissionCategoryOf = (row) => {
  const commissionType = text(row?.CommissionType ?? row?.commissionType);
  if (/on[\s-]*going/.test(commissionType)) return "ongoing";

  const mappedCategory = text(
    row?.RosemountCategory ?? row?.PlanCategory ?? row?.planCategory,
  );
  const mappedMatch = COMMISSION_CATEGORIES.find(
    ({ id, label }) => text(id) === mappedCategory || text(label) === mappedCategory,
  );
  if (mappedMatch && mappedMatch.id !== "ongoing") return mappedMatch.id;

  const businessType = text(row?.BusinessType ?? row?.businessType);
  if (businessType.includes("lifetime")) return "lifetime";
  if (businessType.includes("mortgage")) return "mortgage";
  if (businessType.includes("protection")) return "protection";
  if (businessType === "gi" || businessType.includes("general insurance")) return "gi";
  if (businessType.includes("pension")) return "pension";
  if (businessType.includes("investment")) return "investment";
  if (businessType.includes("long term care")) return "longTermCare";
  if (businessType.includes("bridging") || businessType.includes("commercial")) {
    return "bridgingAndCommercial";
  }
  return "other";
};

const dedupePart = (value) => text(value).replace(/\s+/g, " ");
const dedupeDate = (value) => {
  const source = String(value || "").trim();
  const uk = source.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (uk) {
    const year = uk[3].length === 2 ? `20${uk[3]}` : uk[3];
    return `${year}-${uk[2].padStart(2, "0")}-${uk[1].padStart(2, "0")}`;
  }
  const shortMonth = source.match(/^(\d{1,2})\s+([a-z]{3,9})\s+(\d{2}|\d{4})$/i);
  if (shortMonth) {
    const month = [
      "jan", "feb", "mar", "apr", "may", "jun",
      "jul", "aug", "sep", "oct", "nov", "dec",
    ].indexOf(shortMonth[2].slice(0, 3).toLowerCase());
    if (month >= 0) {
      const year = shortMonth[3].length === 2
        ? `20${shortMonth[3]}`
        : shortMonth[3];
      return `${year}-${String(month + 1).padStart(2, "0")}-${shortMonth[1].padStart(2, "0")}`;
    }
  }
  const iso = source.match(/^(\d{4}-\d{2}-\d{2})/);
  return iso?.[1] || dedupePart(source);
};
export const commissionRowKey = (row) => [
  row?.__statementMonth,
  dedupeDate(row?.PaymentCreated ?? row?.paymentCreated),
  row?.PolicyAdviser ?? row?.policyAdviser,
  row?.ClientName ?? row?.clientName,
  row?.PolicyAccountNumber ?? row?.policyAccountNumber,
  row?.PolicyProvider ?? row?.policyProvider,
  row?.BusinessType ?? row?.businessType,
  row?.CommissionType ?? row?.commissionType,
  amountOf(row).toFixed(2),
].map(dedupePart).join("|");

export const dedupeCommissionRows = (rows = []) => {
  const sourcesByRow = new Map();
  const paymentIds = new Set();
  return rows.filter((row) => {
    const source = text(row?.__source ?? row?.Source) || "unknown";
    const paymentId = String(
      row?.PaymentId ?? row?.Id ?? row?.id ?? row?.Reference ?? "",
    ).trim();
    if (paymentId) {
      const paymentKey = `${source}|${paymentId}`;
      if (paymentIds.has(paymentKey)) return false;
      paymentIds.add(paymentKey);
    }
    const key = commissionRowKey(row);
    const existingSources = sourcesByRow.get(key);
    if (!existingSources) {
      sourcesByRow.set(key, new Set([source]));
      return true;
    }
    if (existingSources.has(source)) return true;
    existingSources.add(source);
    return false;
  });
};

export const buildCommissionBreakdown = (rows = []) => {
  const result = Object.fromEntries(
    COMMISSION_CATEGORIES.map(({ id }) => [id, { count: 0, amount: 0 }]),
  );

  const uniqueRows = dedupeCommissionRows(rows);
  uniqueRows.forEach((row) => {
    const category = commissionCategoryOf(row);
    result[category].count += 1;
    result[category].amount += amountOf(row);
  });

  return {
    categories: result,
    total: uniqueRows.reduce((sum, row) => sum + amountOf(row), 0),
  };
};
